import {
  Button,
  Group,
  Icon,
  Text,
  openConfirmModal,
  useShowNotification,
} from '@inflearn/ds-react-next';
import { faPlus } from '@inflearn/pro-regular-svg-icons';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { logger } from '../../../../../../../resources/Logger';
import { INFLEARN_URL } from '../../../../../../constants/urls';
import useClientAccountProfileQuery from '../../../../../../queries/useClientAccountProfileQuery';
import { useDashboardsRecentLearningQuery } from '../../../../../../queries/useDashboardsRecentLearningQuery';
import useMediaQuery from '../../../../../../styles/useMediaQuery';
import { eventNames } from '../../../../../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../../../../../utils/GTM/useTrackMixpanelEvent';
import { type DeviceData, styleForbidden, styleRegisterable } from '../DeviceManagementPage';
import { DeviceItemLayout } from './DeviceItemLayout';
import { mapIcon } from './deviceUtils';
import useDeviceRegisterCheck from './useDeviceRegisterCheck';

interface DeviceRegisterUiProps {
  device: DeviceData;
  isForbidden?: boolean;
  onRegisterSuccess?: () => void;
  deviceCount?: number;
}

export function DeviceRegisterUi({
  device,
  isForbidden,
  onRegisterSuccess,
  deviceCount,
}: DeviceRegisterUiProps) {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const router = useRouter();
  const { trackMixpanelEvent } = useTrackMixpanelEvent();
  const isFromLecture = router.query.from === 'lecture';
  const { data: accountProfile } = useClientAccountProfileQuery();
  const { data: recentLearning } = useDashboardsRecentLearningQuery({
    options: {
      enabled: isFromLecture,
    },
  });

  const openModal = () => {
    openConfirmModal({
      title: t('마이페이지_기기관리_등록완료_타이틀'),
      children: t('마이페이지_기기관리_등록완료_설명'),
      labels: {
        confirm: isFromLecture ? t('마이페이지_기기관리_등록완료_설명_이어학습') : undefined,
        cancel: t('닫기'),
      },
      size: 'sm',
      withCloseButton: true,
      footerType: 'vertical',
      confirmProps: {
        variant: 'filled',
        color: 'infgreen',
        type: 'submit',
        size: 'md',
        fullWidth: true,
      },
      onConfirm: () => {
        if (!recentLearning) {
          return;
        }

        // 이어학습하기 클릭 이벤트 트래킹
        trackMixpanelEvent(eventNames.CLICK_RECENT_MY_DEVICE, {
          device_count: String(deviceCount || 0),
        });

        // 바로 이동했을 때 이벤트가 잡히지 않는 이슈
        setTimeout(() => {
          location.href = INFLEARN_URL.COURSES_LECTURE({
            courseId: recentLearning.courseId,
            unitId: recentLearning.unitId,
          });
        }, 100);
      },
    });
  };
  const { showNotification } = useShowNotification();
  const openNotification = () => {
    showNotification({
      type: 'success',
      title: t('마이페이지_기기관리_등록완료_타이틀'),
      message: t('마이페이지_기기관리_등록완료_설명_노티'),
    });
  };

  const [isLoading, loadingHandlers] = useDisclosure(false);
  const { checkDeviceRegister } = useDeviceRegisterCheck();
  /**
   * 강의실에서 5개 제한 걸려서 온 경우
   *  - 이어학습하기 버튼이 있는 모달을 띄움
   * 바로 기기 관리 페이지 접근한 경우
   *  - 노티만 띄움
   */
  const handleDeviceRegister = async () => {
    try {
      if (!accountProfile) {
        return;
      }

      loadingHandlers.open();
      await checkDeviceRegister({ userId: accountProfile?.id });

      if (isFromLecture) {
        onRegisterSuccess?.();
        openModal();
        loadingHandlers.close();
        return;
      }
      openNotification();
      onRegisterSuccess?.();
    } catch (error) {
      showNotification({
        type: 'error',
        title: '기기 등록 실패',
        message: error ? (error as Error).message : undefined,
      });
      logger.error(error);
    } finally {
      loadingHandlers.close();
    }
  };

  return (
    <DeviceItemLayout
      styleWrapper={isForbidden ? styleForbidden : styleRegisterable}
      leading={
        <Icon icon={mapIcon(device.deviceType)} size={isMobile ? 'xl' : '2xl'} color="gray.5" />
      }
      content={
        <>
          <Text fz={isMobile ? 'md' : 'lg'} fw={700} lh={1.3} c="gray.9">
            {device.deviceLabel}
          </Text>
          <Group gap={4} mt={4}>
            {isForbidden ? (
              <Text fz={isMobile ? 'xs' : 'sm'} fw={400} lh={1.5} c="red.7">
                {t('마이페이지_기기관리_등록메시지_삭제후등록가능')}
              </Text>
            ) : (
              <Text fz={isMobile ? 'xs' : 'sm'} fw={400} lh={1.5} c="infgreen.7">
                {t('마이페이지_기기관리_등록메시지_자동등록')}
              </Text>
            )}
          </Group>
        </>
      }
      action={
        isForbidden ? (
          <Button
            leftSection={<Icon icon={faPlus} size="xs" color="gray.5" />}
            variant="default"
            color="infgreen"
            size="xs"
            disabled
            radius="md"
          >
            {t('마이페이지_기기관리_액션_등록')}
          </Button>
        ) : (
          <Button
            loading={isLoading}
            onClick={handleDeviceRegister}
            leftSection={<Icon icon={faPlus} size="xs" color="white" />}
            variant="filled"
            color="infgreen"
            size="xs"
            radius="md"
          >
            {t('마이페이지_기기관리_액션_바로등록')}
          </Button>
        )
      }
    />
  );
}
